import { useCallback } from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import withSizes from 'react-sizes';
import { getAgnosticUserPlaces, getSelectedPlace } from 'selectors';
import { MdUnfoldMore } from 'react-icons/md';
import Drawer from 'components/Drawer';
import { ButtonReset, size, spacing } from 'ui';
import { closePlaceSwitcher, openPlaceSwitcher, selectPlace } from 'actions/ui';
import { useTranslation } from 'react-i18next';
import { getPaginatePlaces } from '../actions/places';
import { Spinner, Stack } from '@tymate/margaret';
import { Button, capitalize } from '@tymate/sergic';

const PlaceImage = styled.span`
  display: inline-block;
  width: ${spacing(2)};
  height: ${spacing(2)};
  background-image: url(${props => props.backgroundURL});
  background-size: cover;
  background-position: center center;
  border-radius: ${({ theme }) => theme.borderRadius};
  margin-right: ${spacing(0.25)};
`;

const SelectPlaceButton = styled(ButtonReset)`
  display: flex;
  text-align: left;
  align-items: center;
  width: calc(100% + ${spacing(2)});
  justify-content: flex-start;
  margin-left: ${spacing(-1)};
  margin-right: ${spacing(-1)};
  padding: ${spacing(0.5)} ${spacing()};

  &:hover {
    background-color: ${({ theme }) => theme.background};
  }
`;

const MobilePlaceSwitcher = ({ width }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const { selectedPlace, places, isOpen, pagination, isFetching } = useSelector(
    state => ({
      selectedPlace: getSelectedPlace(state),
      places: getAgnosticUserPlaces(state),
      isOpen: state.ui.placeSwitcherIsOpen,
      pagination: state.places.placesPagination,
      isFetching: state.places.isFetching,
    }),
  );

  const dispatchClosePlaceSwitcher = useCallback(
    () => dispatch(closePlaceSwitcher()),
    [dispatch],
  );
  const dispatchOpenPlaceSwitcher = useCallback(
    () => dispatch(openPlaceSwitcher()),
    [dispatch],
  );
  const fetchPlaces = useCallback(
    page => dispatch(getPaginatePlaces(false, { page: page || 1 })),
    [dispatch],
  );

  const handleSelectPlace = placeId => () => {
    dispatch(selectPlace(placeId));
    dispatchClosePlaceSwitcher();
  };

  if (width > 799 || !selectedPlace || places.length < 2) {
    return null;
  }

  return (
    <>
      <ButtonReset
        style={{ display: 'flex', alignItems: 'center' }}
        onClick={dispatchOpenPlaceSwitcher}
      >
        <PlaceImage backgroundURL={selectedPlace.pictureUrl} />
        <MdUnfoldMore color="#fff" size={size(1)} />
      </ButtonReset>

      <Drawer
        shown={Boolean(isOpen)}
        onRequestClose={dispatchClosePlaceSwitcher}
        side="left"
        title={t('mobilePlaceSwitcher')}
      >
        {places?.map(place => (
          <SelectPlaceButton onClick={handleSelectPlace(place.id)}>
            <PlaceImage backgroundURL={place.pictureUrl} />
            {capitalize(place.displayName)}
          </SelectPlaceButton>
        ))}

        {isFetching && <Spinner />}

        {pagination?.nextPage && (
          <Stack size="full" alignX="center">
            <Button onClick={() => fetchPlaces(pagination?.nextPage)}>
              Voir plus
            </Button>
          </Stack>
        )}
      </Drawer>
    </>
  );
};

export default withSizes(({ width }) => ({ width }))(MobilePlaceSwitcher);
